import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/ru";

import { type IDatePickerProps } from "components/Inputs/Date";
import lang from "lang";

/** Секунд в сутках */
const SECONDS_IN_DAY = 24 * 60 * 60;
/** Секунд в месяце (условно 30 дней) */
const SECONDS_IN_MONTH = 30 * SECONDS_IN_DAY;
/** Секунд в году (условно 365 дней) */
const SECONDS_IN_YEAR = 365 * SECONDS_IN_DAY;

/**
 * Получает информацию о смещении часового пояса для текущей системы в минутах.
 * @returns {{ offsetMinutes: number; offsetHours: number } | null} Объект со смещением в минутах и часах, или null если дата невалидна.
 */
export const getUserTimezoneOffsetMinutes = (): number => {
    const offsetMinutes = dayjs().toDate().getTimezoneOffset();
    return offsetMinutes;
};

export function diffLocalTimeZone(date: Date) {
    return new Date(date.getTime() - new Date().getTimezoneOffset() * 60000);
}
export function checkDate(dateValue: unknown) {
    if (typeof dateValue === "object" && typeof (dateValue as any)?.getMonth === "function") {
        if (dateValue) {
            if (dateValue?.toString() !== "Invalid Date") {
                return true;
            }
        }
    }
    return false;
}
export function dateTime(
    dateTime?: string | Date | number | null,
    format: string = "DD.MM.YYYY HH:mm"
): string {
    let result = "";
    if (dateTime) {
        const date = dayjs(dateTime).format(format);
        if (date) {
            result = date.toString();
        }
    }

    return result;
}

/**
 * Приводит дату к первому дню месяца в формате DD.MM.YYYY.
 * Строки вида DD.MM.YYYY разбираются явно, чтобы dayjs не принял их как MM.DD.YYYY.
 * @param {string | Date | number} value - Дата (ISO, Date или DD.MM.YYYY).
 * @returns {string} Первый день месяца в формате DD.MM.YYYY.
 */
export function toFirstDayOfMonth(value: string | Date | number): string {
    const datePattern = /^\d{2}\.\d{2}\.\d{4}$/;
    if (typeof value === "string" && datePattern.test(value)) {
        const [day, month, year] = value.split(".");
        return dayjs(`${year}-${month}-${day}`).startOf("month").format("DD.MM.YYYY");
    }
    return dayjs(value).startOf("month").format("DD.MM.YYYY");
}

/**
 * Преобразует количество секунд в строку времени вида "HH:mm:ss" или "mm:ss".
 * @param {number} sec - Количество секунд.
 * @param {boolean} full - Флаг, указывающий, нужно ли возвращать полное время.
 * @returns {string} Строка времени.
 */
export function secondToTimeString(sec: number, full: boolean = false): string {
    sec = Math.floor(sec);
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec - hours * 3600) / 60);
    const seconds = sec - hours * 3600 - minutes * 60;
    let result = "";
    if (hours > 0 || full) {
        result += (hours < 10 ? "0" : "") + hours.toString() + ":";
        result += (minutes < 10 ? "0" : "") + minutes.toString() + ":";
        result += (seconds < 10 ? "0" : "") + seconds.toString();
        return result;
    }
    result += (minutes < 10 ? "0" : "") + minutes.toString() + ":";
    result += (seconds < 10 ? "0" : "") + seconds.toString();
    return result;
}

/**
 * Преобразует количество секунд в строку длительности с годами, месяцами и днями.
 * Месяц считается как 30 дней, год — как 365 дней.
 * @param {number} sec - Количество секунд.
 * @param {boolean} full - Показывать нулевые единицы начиная с часов.
 * @returns {string} Строка вида "1мес. 2д. 14ч. 5м. 30с.".
 */
export function secondToDurationString(sec: number, full: boolean = false): string {
    sec = Math.floor(Math.max(0, sec));

    const years = Math.floor(sec / SECONDS_IN_YEAR);
    sec %= SECONDS_IN_YEAR;
    const months = Math.floor(sec / SECONDS_IN_MONTH);
    sec %= SECONDS_IN_MONTH;
    const days = Math.floor(sec / SECONDS_IN_DAY);
    sec %= SECONDS_IN_DAY;
    const hours = Math.floor(sec / 3600);
    sec %= 3600;
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;

    const units = lang.system.dateTime.duration;
    const parts: string[] = [];
    if (years > 0) {
        parts.push(`${years}${units.year}`);
    }
    if (months > 0) {
        parts.push(`${months}${units.month}`);
    }
    if (days > 0) {
        parts.push(`${days}${units.day}`);
    }
    if (hours > 0 || full) {
        parts.push(`${hours}${units.hour}`);
    }
    if (minutes > 0 || full) {
        parts.push(`${minutes}${units.minute}`);
    }
    if (seconds > 0 || full || parts.length === 0) {
        parts.push(`${seconds}${units.second}`);
    }
    return parts.join(" ");
}

/**
 * Преобразует строку времени вида "HH:mm:ss" или "mm:ss" в количество секунд.
 * @param {string} timeStr - Строка времени (например, "10:24:59" или "24:59").
 * @returns {number} Количество секунд.
 */
export const timeStringToSeconds = (timeStr: string): number => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const parts = timeStr.split(":").map(Number);
    if (parts.some(isNaN)) return 0;
    if (parts.length === 3) {
        // Формат HH:mm:ss
        const [hours, minutes, seconds] = parts;
        return hours * 3600 + minutes * 60 + seconds;
    }
    if (parts.length === 2) {
        // Формат mm:ss
        const [minutes, seconds] = parts;
        return minutes * 60 + seconds;
    }
    if (parts.length === 1) {
        // Только секунды
        return parts[0];
    }
    return 0;
};

/**
 * Добавляет смещение часового пояса пользователя к указанной дате
 * @param {string | Date | Dayjs} [baseDate] - Базовая дата (по умолчанию текущая дата)
 * @returns {Dayjs} Дата с примененным смещением часового пояса пользователя
 */
export function addUserTimezoneOffset(
    baseDate?: string | Date | Dayjs,
    isAdd: boolean = true
): Date {
    const date = baseDate ? dayjs(baseDate) : dayjs();
    const offsetMinutes = getUserTimezoneOffsetMinutes();

    // getTimezoneOffset() возвращает смещение в обратном направлении
    // (положительное значение для часовых поясов западнее UTC)
    // поэтому вычитаем смещение для получения локального времени
    if (isAdd) {
        return date.add(offsetMinutes, "minute").toDate();
    }
    return date.subtract(offsetMinutes, "minute").toDate();
}

/**
 * Преобразует дату в зависимости от timeChange
 * @param date - Дата
 * @param timeChange - Тип преобразования
 * @returns Дата
 */
export function dateTransform(
    date: "string" | Date | Dayjs | null,
    timeChange: IDatePickerProps["timeChange"]
): Dayjs | null {
    if (!date) {
        return null;
    }
    if (date instanceof Date || typeof date === "string") {
        date = dayjs(date);
    }
    switch (timeChange) {
        case "startDay":
            return date.startOf("day");
        case "endDay":
            return date.endOf("day");
    }
    return date;
}
/**
 * Возвращает название месяца по номеру в строке ("01" → "январь").
 */
export function monthNumberToName(month: string, mode: "short" | "long" = "long"): string {
    const m = month.padStart(2, "0");
    const d = dayjs(`2024-${m}-01`).locale(lang.localeName);
    return d.format(mode === "short" ? "MMM" : "MMMM");
}

export function formatDatePeriod(
    startDate: string | Date | Dayjs | null,
    endDate: string | Date | Dayjs | null,
    format: {
        day?: "numeric" | "2-digit";
        month?: "numeric" | "2-digit" | "long" | "short" | "narrow";
        year?: "numeric" | "2-digit";
    } = { day: "numeric", month: "long", year: "numeric" }
): string {
    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;

    const isStartValid = start && start.isValid();
    const isEndValid = end && end.isValid();

    // 1. Fallback: both fields are empty or invalid
    if (!isStartValid && !isEndValid) {
        return "";
    }

    // Generate customized formatters dynamically from your incoming base options
    const formatterWithYear = new Intl.DateTimeFormat(lang.localeName, format);

    const formatterNoYear = new Intl.DateTimeFormat(lang.localeName, {
        ...format,
        year: undefined, // Strip the year token for combined months
    });

    const monthYearFormatter = new Intl.DateTimeFormat(lang.localeName, {
        ...format,
        day: undefined, // Strip day token to grab pure localized text segment
    });

    // 2. Only end date is available
    if (!isStartValid && isEndValid) {
        return formatterWithYear.format(end.toDate());
    }

    const startJsDate = (start as Dayjs).toDate();

    // 3. Only start date is available (or both boundaries are on identical days)
    if (!isEndValid || (start as Dayjs).isSame(end, "day")) {
        return formatterWithYear.format(startJsDate);
    }

    const endJsDate = (end as Dayjs).toDate();

    // 4. Same month and same year (e.g., "1–30 сентября 2026 г." or "1–3 авг. 26 г.")
    if ((start as Dayjs).isSame(end, "month") && (start as Dayjs).isSame(end, "year")) {
        const monthYearStr = monthYearFormatter.format(startJsDate).trim();
        return `${(start as Dayjs).date()}–${(end as Dayjs).date()} ${monthYearStr}`;
    }

    // 5. Different months, but same year (e.g., "28 окт. – 5 нояб. 26 г.")
    if ((start as Dayjs).isSame(end, "year")) {
        const startStr = formatterNoYear.format(startJsDate).trim();
        const endStr = formatterWithYear.format(endJsDate).trim();
        return `${startStr} – ${endStr}`;
    }

    // 6. Different years (e.g., "30 дек. 2026 г. – 2 янв. 2027 г.")
    const startStr = formatterWithYear.format(startJsDate).trim();
    const endStr = formatterWithYear.format(endJsDate).trim();
    return `${startStr} – ${endStr}`;
}
